import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

const DISTRICTS = [
  { id: 'westtripura', name: 'West Tripura', homestays: 280, valleys: ['Ujjayanta Palace', 'Heritage Park', 'Chaturdash Devta', 'Agartala'], altitude: '15–50m', highlight: true, division: 'Western' },
  { id: 'sepahijala', name: 'Sepahijala', homestays: 120, valleys: ['Sepahijala WLS', 'Clouded Leopard NP', 'Neermahal Palace', 'Melaghar'], altitude: '15–100m', highlight: true, division: 'Central' },
  { id: 'northtripura', name: 'North Tripura', homestays: 110, valleys: ['Unakoti', 'Jampui Hills', 'Dharmanagar', 'Kailashahar'], altitude: '15–950m', highlight: true, division: 'Northern' },
  { id: 'gomati', name: 'Gomati', homestays: 100, valleys: ['Tepania Eco Park', 'Dumboor Lake', 'Tirthamukh', 'Udaipur'], altitude: '15–300m', highlight: true, division: 'Southern' },
  { id: 'dhalai', name: 'Dhalai', homestays: 90, valleys: ['Kamalpur', 'Gandacherra', 'Raima Valley', 'Longtharai'], altitude: '15–500m', highlight: false, division: 'Central' },
  { id: 'unakoti', name: 'Unakoti', homestays: 80, valleys: ['Unakoti Rock Carvings', 'Chhabimura', 'Kailashahar', 'Rangamati'], altitude: '15–400m', highlight: true, division: 'Northern' },
  { id: 'southtripura', name: 'South Tripura', homestays: 70, valleys: ['Sabroom', 'Pilak', 'Matabari', 'Fenny River'], altitude: '15–200m', highlight: false, division: 'Southern' },
  { id: 'khowai', name: 'Khowai', homestays: 60, valleys: ['Khowai River', 'Kalyanpur', 'Teliamura', 'Tulashikhar'], altitude: '15–150m', highlight: false, division: 'Western' },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'State-Licensed Homestays',
    desc: 'Every homestay registered with the Tripura Tourism Department, from Agartala\'s heritage quarters to the Jampui Hills, digitized and discoverable.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on district map', 'Basic contact listing', 'Searchable by district & valley'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays editorially visited and quality-verified. Verified palace-view stays, lake retreats, and traveller-ready listings.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing, direct booking, and dedicated account management. The hidden gems of India\'s northeast corner.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay from Agartala\'s royal quarters to Jampui\'s orange groves — searchable, verified, bookable on one platform.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, Garia Puja festival trends, and heritage-tourism occupancy insights for Tripura tourism planning.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy — empowering Bengali, Tripuri, and Reang tribal communities to open their heritage homes.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification ensuring each homestay honours the royal Manikya dynasty heritage, bamboo craft traditions, and sacred rock-art sites.' },
];

function TripuraMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    'northtripura': { d: 'M100 20 L230 15 L260 60 L240 110 L170 120 L100 90 L80 50 Z', cx: 170, cy: 65 },
    'unakoti': { d: 'M80 50 L100 90 L90 140 L50 150 L30 110 L40 65 Z', cx: 65, cy: 100 },
    'dhalai': { d: 'M170 120 L240 110 L270 160 L250 210 L190 220 L140 190 L130 150 L100 130 Z', cx: 190, cy: 168 },
    'khowai': { d: 'M50 150 L90 140 L130 150 L140 190 L110 220 L60 215 L35 185 Z', cx: 87, cy: 180 },
    'westtripura': { d: 'M60 215 L110 220 L140 250 L120 290 L70 295 L35 265 Z', cx: 88, cy: 258 },
    'sepahijala': { d: 'M140 190 L190 220 L200 265 L170 295 L120 290 L140 250 L110 220 Z', cx: 160, cy: 255 },
    'gomati': { d: 'M200 265 L250 210 L290 240 L285 290 L250 320 L210 310 L170 295 Z', cx: 235, cy: 280 },
    'southtripura': { d: 'M120 290 L170 295 L210 310 L250 320 L240 360 L180 370 L120 355 L90 330 Z', cx: 175, cy: 335 },
  };

  return (
    <svg viewBox="0 0 320 390" className="w-full max-w-md mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id} onMouseEnter={() => setActiveDistrict(id)} onMouseLeave={() => setActiveDistrict(null)} style={{ cursor: 'pointer' }}>
            <path d={path.d}
              fill={isActive ? '#D4A853' : isHighlight ? 'rgba(212,168,83,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#E8C88A' : 'rgba(255,255,255,0.3)'} strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }} />
            <text x={path.cx} y={path.cy} textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'} fontSize={isActive ? 10 : 8}
              fontFamily="Georgia, serif" style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}>
              {district?.name}
            </text>
            {isActive && (
              <text x={path.cx} y={path.cy + 14} textAnchor="middle" dominantBaseline="middle"
                fill="#E8C88A" fontSize={8} fontFamily="Georgia, serif" style={{ pointerEvents: 'none' }}>
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function TripuraStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="tripura-state-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="tripura" />
        </div>
      </nav>

      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1758939559459-86e57f516ac9?w=1920&q=80"
            alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Tripura &middot; The Royal Heritage</span>
          </div>
          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>
          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 8 Districts &middot; One Platform
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Where palaces float on lakes,<br />and a million gods are carved into stone
          </h2>
        </div>
      </section>

      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-6">The Forgotten Kingdom</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            A land of rock-carved gods,<br />
            lake palaces, and bamboo symphonies.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            At Neermahal, India's only lake palace rises from the waters of Rudrasagar — a 
            Mughal-Hindu fusion dream built by the last Manikya king in 1930, its white domes 
            reflected perfectly in the still water. At Unakoti, a hillside bears the faces of 
            Shiva carved into rock over a thousand years ago — 9,999,999 images, says legend, 
            one short of a crore. In the Jampui Hills, Tripura's highest ridges, Reang tribal 
            villages tend orange orchards above clouds while the annual Hojagiri dance celebrates 
            Laxmi on floating lamps. At Pilak, Buddhist and Hindu ruins from the 8th century lie 
            half-swallowed by the forest, evidence that this was once a crossroads of civilizations. 
            The Ujjayanta Palace in Agartala, now a museum, tells the story of a dynasty that 
            ruled for two thousand years. Tripura is where India's heritage whispers loudest in 
            its quietest corner.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Royal capital to tribal highlands. One kingdom.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative"><TripuraMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} /></div>
            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-[#D4A853] text-[10px] tracking-[0.4em] uppercase">Region</p>
                    <span className="text-white/20 text-[10px]">&middot;</span>
                    <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase">{activeData.division} Tripura</p>
                  </div>
                  <h3 className="text-4xl text-white font-light italic mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.name}</h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.homestays}</span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-white/40" /><span className="text-white/60 text-sm">{activeData.altitude}</span></div>
                    <div className="flex flex-wrap gap-2">{activeData.valleys.map(v => (<span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">{v}</span>))}</div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a region to explore</p>
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.filter(d => d.highlight).map(d => (
                      <div key={d.id} className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer" onMouseEnter={() => setActiveDistrict(d.id)}>
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{d.homestays}</p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across Tripura</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{TOTAL_HOMESTAYS.toLocaleString()} homestays</span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#D4A853] to-[#E8C88A] rounded-full" style={{ width: '100%', animation: 'expandBar 2s ease-out' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Three tiers. One ecosystem.</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">Every homestay begins as Registered. Through quality verification and partnership, they rise — from Silver to Gold.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name} className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group" style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}>
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{i + 1}</div>
                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{tier.name}</h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>{tier.tagline}</p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>
                <ul className="space-y-2.5">{tier.features.map(f => (<li key={f} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} /><span className="text-white/55 text-sm">{f}</span></li>))}</ul>
                {i < 2 && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10"><ArrowRight className="w-5 h-5 text-white/10" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br /><span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">GoHimalayan serves as the technology and marketing partner — bringing every licensed homestay across Tripura's royal heritage and tribal highlands online and discoverable.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#D4A853] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{prop.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>
          <div className="border border-[#D4A853]/20 rounded-xl p-10 sm:p-14 text-center" style={{ background: 'linear-gradient(160deg, rgba(212,168,83,0.05), transparent)' }}>
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Built to scale. Designed to empower.</h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#D4A853]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#D4A853] text-xs">1</span></div>
                <p className="text-white/60 text-sm">Tripura shares its licensed homestay registry — GoHimalayan digitizes and maps every property across 8 districts</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#D4A853]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#D4A853] text-xs">2</span></div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into royal heritage communities, lake villages, and tribal hill homestays</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#D4A853]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#D4A853] text-xs">3</span></div>
                <p className="text-white/60 text-sm">State receives real-time demand intelligence, heritage-tourism analytics, and cultural-corridor planning dashboards</p>
              </div>
            </div>
            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Tripura."
              className="inline-flex items-center gap-3 bg-[#D4A853] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#e0bc74] transition-colors"
              data-testid="b2g-cta" target="_blank" rel="noopener noreferrer">
              Begin Conversation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#D4A853" />
      <style>{`@keyframes expandBar { from { width: 0; } to { width: 100%; } }`}</style>
    </div>
  );
}
